import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { PREVIEW_HASH, envelope, originOf, parseCanvasMessage } from '@yourstore/theme';
import type { PreviewCanvasMessage, PreviewEditorMessage } from '@yourstore/theme';
import { slugForTenantId } from '@yourstore/domain';

/**
 * **The builder's end of the canvas wire** (`packages/theme/src/preview.ts`).
 *
 * The canvas is the business's real site, framed at `/#/__preview`. This hook
 * owns the frame's side of the conversation and nothing else: the two checks
 * every message must pass, the `hello` that pairs a console the site was not
 * told about, and the state the stage draws — connecting, ready, or a reason
 * in words why there is no picture. What the messages *mean* is the builder's.
 *
 * ## The two checks
 *
 * A message counts only when it comes from **the frame's own window** and
 * **the origin the frame was loaded from**. The first rules out every other
 * window that can reach this one (a second tab, an extension, a widget); the
 * second rules out the frame having navigated somewhere else. Posts go to that
 * one origin, never `*`: the draft is a business's unpublished page.
 */

export type CanvasState =
  | { kind: 'off'; reason: 'no-address' }
  | { kind: 'connecting' }
  | { kind: 'ready' }
  | { kind: 'failed'; reason: 'timeout' | 'data-unavailable' | 'version-mismatch' };

/** How long the frame has to answer before the stage says it cannot. */
export const CANVAS_TIMEOUT_MS = 12_000;
/** How often `hello` is repeated while the site's bundle is still loading. */
const HELLO_EVERY_MS = 500;

/**
 * **The address the canvas is loaded from**, for one business.
 *
 * Every business has `{slug}.book.yourstore.<tld>` with no row behind it
 * (spec §6.4.2; the Website tab's address card shows the slug), so the canvas
 * needs no read to find it. The deployment decides the rest:
 *
 *  - `VITE_SITE_PREVIEW_ORIGIN` names it outright — `{slug}` substituted — for
 *    the local rig, where it is the site's dev server (`http://localhost:5175`,
 *    which serves the tenant its `VITE_DEV_TENANT_HOST` names);
 *  - otherwise the console's own hostname says which environment this is:
 *    `admin-test.…` frames `{slug}.book-test.…`, `admin.…` frames `{slug}.book.…`;
 *  - anything else has no canvas, and says so rather than guessing.
 */
export function previewOriginFor(pattern: string, tenantId: string, here: string): string | null {
  const slug = slugForTenantId(tenantId);
  if (pattern !== '') return originOf(pattern.split('{slug}').join(slug));
  let host: string;
  try {
    host = new URL(here).hostname;
  } catch {
    return null;
  }
  const match = /^admin(-test)?\.(.+)$/.exec(host);
  if (match === null) return null;
  const [, test = '', rest = ''] = match;
  return originOf(`https://${slug}.book${test}.${rest}`);
}

/** The canvas's URL on a site origin. */
export function previewUrl(origin: string): string {
  return `${origin}/${PREVIEW_HASH}`;
}

export interface Canvas {
  frame: RefObject<HTMLIFrameElement | null>;
  state: CanvasState;
  /** Post one message to the frame. Dropped, silently, when there is no frame. */
  post: (message: PreviewEditorMessage) => void;
  /** Called by the frame's `load`: start saying hello until it answers. */
  onLoad: () => void;
  /** Load the frame again after a failure. */
  retry: () => void;
  /** Bumped by `retry`, so the frame's `key` changes and it reloads. */
  generation: number;
}

export function useCanvas(
  origin: string | null,
  onMessage: (message: PreviewCanvasMessage) => void,
): Canvas {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<CanvasState>(
    origin === null ? { kind: 'off', reason: 'no-address' } : { kind: 'connecting' },
  );
  const [generation, setGeneration] = useState(0);
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const ready = useRef(false);
  const hello = useRef<number | null>(null);
  const deadline = useRef<number | null>(null);

  const post = useCallback(
    (message: PreviewEditorMessage) => {
      const target = frame.current?.contentWindow;
      if (origin === null || target === null || target === undefined) return;
      target.postMessage(envelope(message), origin);
    },
    [origin],
  );

  const stopHello = (): void => {
    if (hello.current !== null) window.clearInterval(hello.current);
    hello.current = null;
  };
  const stopDeadline = (): void => {
    if (deadline.current !== null) window.clearTimeout(deadline.current);
    deadline.current = null;
  };

  const onLoad = useCallback(() => {
    if (ready.current) return;
    post({ type: 'hello' });
    stopHello();
    hello.current = window.setInterval(() => post({ type: 'hello' }), HELLO_EVERY_MS);
  }, [post]);

  // The deadline, from the moment the frame is put up.
  useEffect(() => {
    ready.current = false;
    if (origin === null) {
      setState({ kind: 'off', reason: 'no-address' });
      return undefined;
    }
    setState({ kind: 'connecting' });
    deadline.current = window.setTimeout(() => {
      if (!ready.current) {
        stopHello();
        setState({ kind: 'failed', reason: 'timeout' });
      }
    }, CANVAS_TIMEOUT_MS);
    return () => {
      stopHello();
      stopDeadline();
    };
  }, [origin, generation]);

  useEffect(() => {
    if (origin === null) return undefined;
    const listen = (event: MessageEvent): void => {
      const own = frame.current?.contentWindow;
      if (own === null || own === undefined || event.source !== own) return;
      if (event.origin !== origin) return;
      const parsed = parseCanvasMessage(event.data);
      if (!parsed.ok) return;
      const message = parsed.message;
      if (message.type === 'ready') {
        ready.current = true;
        stopHello();
        stopDeadline();
        setState({ kind: 'ready' });
      } else if (message.type === 'error' && message.code === 'data-unavailable') {
        setState({ kind: 'failed', reason: 'data-unavailable' });
      } else if (message.type === 'error' && message.code === 'version-mismatch') {
        setState({ kind: 'failed', reason: 'version-mismatch' });
      }
      handler.current(message);
    };
    window.addEventListener('message', listen);
    return () => window.removeEventListener('message', listen);
  }, [origin]);

  const retry = useCallback(() => setGeneration((n) => n + 1), []);

  return { frame, state, post, onLoad, retry, generation };
}
